import { css } from '@emotion/react';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import {
  useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { InputSize } from '~/_shared/baseComponents/inputs';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { HintTooltipComponent } from '~/_shared/baseComponents/tooltip/hintTooltip.component';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { hexColorWithOpacityToString } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { MapColumnPickerDropdownContainer } from '~/_shared/components/mapColumnPickerDropdown/mapColumnPickerDropdown.container';
import { RadarMarkerDot } from '~/_shared/components/markers/animatedRadarMarker/animatedRadarMarker.component';
import { MapConfirmComponent } from '~/_shared/mapConfirm/mapConfirm.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import type { LatLng } from '~/_shared/types/latLng';
import type { ColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { copyToClipboard } from '~/_shared/utils/clipboard/clipboard.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { useTimeout } from '~/_shared/utils/hooks/useTimeout';
import { useIsNumericColumnCheck } from '~/heatMap/panel/form/useIsNumericColumnCheck.hook';
import { useWeightedCenterFilter } from '~/store/frontendState/mapTools/weightedCenter/useWeightedCenterFilter';
import {
  weightedCenterClearSelectedEntities, weightedCenterSetWeightColumnId,
} from '~/store/frontendState/mapTools/weightedCenter/weightedCenter.actionCreators';
import {
  useIsWeightedCenterActiveSelector, useWeigtedCenterResultsSelector,
} from '~/store/frontendState/mapTools/weightedCenter/weightedCenter.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';

enum WeightedCenterWeightOption {
  Equal = 'equal',
  Column = 'column',
}

const mapConfirmStyle = css({
  minWidth: 285,
  maxWidth: 360,
});

const errorContainerStyle = (theme: Theme) => css({
  position: 'relative',
  width: 'fit-content',
  background: hexColorWithOpacityToString(theme.modalColors.contentBackground, .7),
  borderRadius: 4,
  boxShadow: `0 0 0 2px ${theme.buttonColors.secondaryBackground}`,
  marginTop: 8,
  marginLeft: 0,
  padding: 10,
});

const errorHighlightStyle = (theme: Theme) => css({
  color: theme.textColors.danger,
});

const centerVerticallyStyle = css({
  alignItems: 'center',
});

const eliminateRadioGroupMarginStyle = css({
  marginBottom: -10,
});

const hintTooltipListStyle = css({
  margin: 0,
  paddingInlineStart: 25,
});

export const WeightedCenterFloatingUIContainer = () => {
  const [t] = useTranslation();
  const isActive = useIsWeightedCenterActiveSelector();
  const dispatch = useDispatch();
  const filters = useWeightedCenterFilter();
  const results = useWeigtedCenterResultsSelector();

  const [weightOption, setWeightOption] = useState<WeightedCenterWeightOption>(WeightedCenterWeightOption.Equal);
  const [isLoading, setIsLoading] = useState(false);

  if (!isActive) {
    return null;
  }

  const onClose = () => {
    dispatch(weightedCenterClearSelectedEntities());
  };

  const onChangeWeightOption = (newValue: WeightedCenterWeightOption) => {
    setWeightOption(newValue);
    if (newValue === WeightedCenterWeightOption.Equal) {
      dispatch(weightedCenterSetWeightColumnId(null));
    }
  };

  const error = results.isError || filters.isEmpty;

  const entityNames = filters.names.join(', ');

  return (
    <MapConfirmComponent
      css={mapConfirmStyle}
      isLoading={results.isLoading || isLoading}
      isOpen
      onClose={onClose}
      heading={t('Weighted Center')}
      buttons={[]}
      radio={{
        heading: <WeightedCenterRadioHeaderComponent name={entityNames} center={results.weightedCenter} />,
        selectedValue: weightOption,
        onChange: (newValue: string) => onChangeWeightOption(newValue as WeightedCenterWeightOption),
        items: [{
          label: t('weightedCenter.weightOptions.equal.caption'),
          value: WeightedCenterWeightOption.Equal,
        }, {
          label: t('weightedCenter.weightOptions.column.caption'),
          value: WeightedCenterWeightOption.Column,
          children: weightOption === WeightedCenterWeightOption.Column && (
            <WeightedCenterColumnSelectorContainer onLoadingChange={setIsLoading} />
          ),
        }],
      }}
      secondRow={error ? <WeightedCenterErrorComponent /> : undefined}
    />
  );
};

const WeightedCenterRadioHeaderComponent = ({
  name,
  center,
}: Readonly<{
  name: string;
  center: LatLng | null;
}>) => {
  const [t] = useTranslation();
  const theme = useTheme();
  const [isCopiedToClipboard, showCopiedToClipboardMessage] = useTimeout(3000);

  const onCopyClick = () => {
    if (!center) {
      return;
    }

    copyToClipboard(`${center.lat.toFixed(6)}, ${center.lng.toFixed(6)}`);
    showCopiedToClipboardMessage();
  };

  return (
    <Stack gap={20} css={eliminateRadioGroupMarginStyle}>
      <Stack gap={4}>
        <div>{name}</div>
        <Inline gap={2} css={centerVerticallyStyle}>
          <RadarMarkerDot />
          <span>
            {center ? `${'Lat:'} ${center.lat.toFixed(6)}, ${'Lng:'} ${center.lng.toFixed(6)}` : t('Calculating...')}
          </span>
          {center && (
            <TooltipComponent
              tooltipContent={isCopiedToClipboard ? t('Copied to Clipboard!') : t('Copy to Clipboard')}
              behavior={isCopiedToClipboard ? TooltipBehavior.ShowAlways : TooltipBehavior.ShowOnHover}
            >
              <HoverableIconComponent
                baseColor={theme.iconColors.primary}
                hoverColor={theme.iconColors.borderPrimaryHover}
                icon={faCopy}
                title={t('Copy Coordinates to Clipboard')}
                onClick={onCopyClick}
              />
            </TooltipComponent>
          )}
        </Inline>
      </Stack>
      <Inline gap={4} css={centerVerticallyStyle}>
        <span>{t('weightedCenter.weightMethods.choice.caption')}</span>
        <HintTooltipComponent
          size={16}
          tooltipContent={(
            <ul css={hintTooltipListStyle}>
              <li>{t('weightedCenter.weightOptions.equal.explanation')}</li>
              <li>{t('weightedCenter.weightOptions.column.explanation')}</li>
            </ul>
          )}
        />
      </Inline>
    </Stack>
  );
};

const WeightedCenterErrorComponent = () => {
  const [t] = useTranslation();

  return (
    <Stack
      gap={4}
      css={errorContainerStyle}
    >
      <span css={errorHighlightStyle}>{t('weightedCenter.error.cannotCalculate.message')}</span>
      <span>{t('Please try again. If this problem persists, please contact the customer support.')}</span>
    </Stack>
  );
};

const dropdownErrorStyle = (theme: Theme) => css({
  border: `1px solid ${theme.borderColors.error}`,
});

const WeightedCenterColumnSelectorContainer = ({
  onLoadingChange,
}: Readonly<{
  onLoadingChange: (isLoading: boolean) => void;
}>) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const spreadsheetId = usePrimarySpreadsheetId();

  const { isLoading, checkIsNumeric } = useIsNumericColumnCheck();

  const [invalidColumnError, setInvalidColumnError] = useState(false);

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  if (!mapId || !spreadsheetId) {
    return null;
  }

  const onChange = async (columnId: ColumnId) => {
    const isNumeric = await checkIsNumeric(columnId, spreadsheetId);
    if (!isNumeric) {
      setInvalidColumnError(true);
      return;
    }

    setInvalidColumnError(false);
    dispatch(weightedCenterSetWeightColumnId(columnId));
  };

  return (
    <Stack css={{ marginTop: 4 }} gap={4}>
      <MapColumnPickerDropdownContainer
        mapId={mapId}
        virtualSpreadsheetId={spreadsheetId}
        onChange={onChange}
        triggerHeight={InputSize.Medium}
        triggerStyle={invalidColumnError ? dropdownErrorStyle : undefined}
        placeholder={t('Select Data Column')}
      />
      {invalidColumnError && (
        <span css={errorHighlightStyle}>{t('Not a numerical column')}</span>
      )}
    </Stack>
  );
};
